import _ from 'lodash'
import { EMPTY, Observable, of, throwError } from 'rxjs'
import { catchError, delayWhen, distinctUntilChanged, first, last, map, pluck, tap } from 'rxjs/operators'

import { DataStore, DataStoreOptions, OfflineDataFetcher, SearchResponse } from "@skinsoft/data-store"
import { ItemDataStore, itemSelectors, ThesaurusTermDataStore, thesaurusTermSelectors } from '@skinsoft/entity'
import { fetch } from '@skinsoft/network'
import { getTranslatedString } from '@skinsoft/ui'

declare const FALLBACK_LOCALE;

export interface EntityField {
    id: string,
    isReadOnly: boolean,
    entity: string,
    abstractEntity: string,
    schema: string,
    name: string,
    type: any,
    path: string,
    inputComponent: {
        name: string
    },
    entityFields: any[]
}

export class EntityFieldDataStore extends DataStore<EntityField> {

    /**
     * Retrieve string value from entity and entityField (useful to sort fields)
     * @param value 
     * @param entityField 
     * @param locale 
     */
    public static async getValueFromEntityField(value: any, entityField: EntityField, locale: string = FALLBACK_LOCALE): Promise<string> {
        const efPath = _.get(entityField, 'path')
        const efIsReference = _.get(entityField, 'type.referenceField')
        const efType = _.get(entityField, 'type.type')

        const rawValue = _.get(value, efPath);

        if (!efIsReference) {
            switch (efType) {
                case "String":
                    return rawValue as string;
                case "TranslatedString":
                    {
                        const returnValue = getTranslatedString(rawValue, locale) as string || "";
                        return returnValue;
                    }
                case "SkinDate":
                    {
                        const returnValue: number = _.get(rawValue, 'startInterval.date') as number;
                        return returnValue ? returnValue.toString() : "0";
                    }
                default:
                    return "";
            }
        } else {
            switch (efType) {
                case "Item":
                    const item = await ItemDataStore.getInstance().fetchOne(rawValue).pipe(first()).toPromise();
                    return itemSelectors.title(item, locale); // Maybe improve ?
                case "ThesaurusTerm":
                    const term = await ThesaurusTermDataStore.getInstance().fetchOne(rawValue).pipe(first()).toPromise();
                    const returnValue = getTranslatedString(thesaurusTermSelectors.preferredTerm(term), locale) || "";
                    return returnValue;
                // TODO other entity types if needed
                default:
                    return ""
            }
        }
    }

    public static getInstance() {
        if (!EntityFieldDataStore.instance) {
            EntityFieldDataStore.instance = new EntityFieldDataStore();
        }

        return EntityFieldDataStore.instance;
    }

    public static install(Vue) {
        Vue.prototype.$entityFieldDataStore = EntityFieldDataStore.getInstance();
    }

    private static instance: EntityFieldDataStore = null as any;

    constructor() {
        super('/entityField', ['physicalMuseumItem', 'recolement', 'auditProject'])
        this.emulateSearch = true;
    }

    /**
     * We cannot search on entityFields so we override searchAll method to emulate the search behavior.
     * Each query for a different entity type is considered as a search "page"
     * @param queryText 
     * @param sortObject 
     * @param pageSize 
     * @param types 
     * @param page 
     * @param advancedSearches 
     * @param filteredIds 
     * @param pathAndValueToCheck 
     * @param options 
     */
    public searchAll(queryText: string | null, sortObject: any, pageSize: number, types: string[], page: number = 0, advancedSearches: any = null, filteredIds?: string[], pathAndValueToCheck?: any, searchEndpointOverride: string | null = null, options: DataStoreOptions = {}): Observable<SearchResponse<EntityField>> {
        if (page < types.length) {
            const url = `${this.endpoint}/${types[page]}`
            return this.proxy.fetch(url)
                .pipe(
                    pluck("_embedded", "entityFields"),
                    map(
                        data => {
                            return (
                                {
                                    results: data,
                                    totalSize: types.length
                                } as SearchResponse<EntityField>
                            )
                        }
                    )
                )
        }
        else {
            return of({
                allData: [],
                results: [],
                totalSize: types.length,
            } as SearchResponse<EntityField>)
        }
    }

    public async getEntityFieldId(entityFieldName: string,
        entityName: string,
        schemaName: string): Promise<string> {

        // Online
        if (this.offlineDataFetcher && window.navigator.onLine) {
            const entityField = await this.proxy.fetch(`${this.endpoint}/name`, {
                entityFieldName,
                entityName,
                schemaName
            }).toPromise();
            this.offlineDataFetcher.updateOne(entityField.id, entityField)
            return entityField.id;
        }
        // Offline
        else {
            const matchingEntityField = await this.offlineDataFetcher.fetchOneFromPredicate(
                (entityField: EntityField) => {
                    return entityField.entity === entityName && entityField.schema === schemaName && entityField.name === entityFieldName
                }
            ).toPromise();
            if (matchingEntityField) {
                return matchingEntityField.id;
            } else {
                return "";
            }
        }
    }

    public fetchOne(id: string, networkFirst = false): Observable<EntityField> {
        // if online
        if (window.navigator.onLine && !OfflineDataFetcher.isTemporaryId(id)) {
            const uri = `${this.endpoint}/id/${id}`;

            const network$ = this.proxy.fetch(uri, undefined, undefined, undefined, networkFirst).pipe(
                tap(data => this.offlineDataFetcher.updateOne(data.id, data)),
                last(),
                catchError(error => (fetch.isNetworkError(error) ? EMPTY : throwError(error))),
            );
            let source$ = network$;

            if (networkFirst) {
                source$ = source$.pipe(delayWhen(() => network$));
            }

            return source$.pipe(
                distinctUntilChanged(_.isEqual),
                this.mergeUpdateCacheDataToOneResult.bind(this),
            );
        }
        // if offline
        else {
            return this.fetchOneOfflineMode(id);
        }
    }
}